import { Dispatch, SetStateAction, useEffect } from 'react';
import { SeriesItemData } from '../../cards/series-item-card/SeriesItemCardProps';
import { SaveConfig } from '../SaveButtonProps';
import { fetchSavedStateThenDispatch } from '../_helpers/save';
import {
	addSavedChangeListener,
	dispatchSavedChangedEvent,
	SavedChangedEventDetail,
} from '../_helpers/savedChangeEvent';
import {
	CookieCategory,
	createCookieToaster,
	isCookieAllowed,
	NO_COOKIES_ACCEPTED_ERROR,
} from '../../../../../_helpers/manageCookies';
import { useComponentContext } from '../../_helpers/componentContext';
import { SavableSeriesItemData } from '../../../../../cms-components/blackrock/advisor-center/saved-page/SavedItemsReducer.ts';

/**
 * Custom hook for savable components with SeriesItem list to handle save related stuff:
 * - Handles saved change events (if the saved state has changed in another component).
 * - Changes and fetches the saved state in the component.
 * @param {Dispatch<SetStateAction<SeriesItemData[]>>} setSeriesItemList - Set function of the SeriesItem list state in the component.
 * @param {SaveConfig} saveConfig - Save configuration of the component.
 * @param {string} subcomponentId - Addition to the component id, in case there are multiple seriesItemList within a component.
 * @return {(item: SeriesItemData) => void} - Function that changes the save state of the given item.
 */
export const useSaveForSeriesItemList = (
	setSeriesItemList: Dispatch<SetStateAction<SeriesItemData[]>>,
	saveConfig: SaveConfig | null,
	subcomponentId: string = ''
): ((item: SavableSeriesItemData) => void) => {
	if (!saveConfig) {
		return () => null;
	}

	const { componentName, componentId } = useComponentContext();

	// Changes the item with the specified saveUrl based on the given updater object
	const updateItems = (
		saveUrl: string,
		updaterObj: Partial<SeriesItemData>
	): SeriesItemData[] | void => {
		//In some edge cases, it is possible that a certain seriesItem appears in the list more than once (e.g. on FeaturedSeries tabs)
		setSeriesItemList((prevState) => {
			return prevState.map((item) =>
				item.saveUrl === saveUrl ? { ...item, ...updaterObj } : item
			);
		});
	};

	// Handles saved change events (if the saved state has changed in another component)
	useEffect(() => {
		const handleSavedChange = (eventDetail: SavedChangedEventDetail) => {
			if (eventDetail.sourceComponentId !== componentId + subcomponentId) {
				updateItems(eventDetail.saveUrl, { saved: eventDetail.newState });
			}
		};
		const removeListener = addSavedChangeListener(handleSavedChange);
		return removeListener;
	}, []);

	// Change the save status regardless of the http request,
	// and if no request is currently in progress, we send a new request.
	// (This is the return value of this custom hook)
	const changeSavedState = (item: SavableSeriesItemData) => {
		if (!isCookieAllowed(CookieCategory.PREFERENCE)) {
			createCookieToaster(saveConfig.translationMap.cookieToaster);
			// It is necessary to change/change back saved state to reset save button state
			const newSavedState = !item.saved;
			updateItems(item.saveUrl, { saved: newSavedState });
			setTimeout(() => {
				updateItems(item.saveUrl, { saved: !newSavedState });
			}, 0);
			return;
		}

		const newSavedState = !item.saved;
		updateItems(item.saveUrl, { saved: newSavedState });
		dispatchSavedChangedEvent(
			item.saveUrl,
			newSavedState,
			componentId + subcomponentId
		);

		if (!item.isSaveRequestInProgress) {
			updateItems(item.saveUrl, { isSaveRequestInProgress: true });
			fetchSavedState(item, newSavedState);
		}
	};

	// Fetch and handle success or error in SeriesItem list
	const fetchSavedState = async (
		item: SavableSeriesItemData,
		newSavedState: boolean
	) => {
		try {
			const response = await fetchSavedStateThenDispatch(
				saveConfig.saveFetchUrl,
				item,
				newSavedState,
				componentName,
				componentId + subcomponentId
			);
			onSaveSuccess(item.saveUrl, newSavedState, response);
		} catch (error) {
			onSaveError(item.saveUrl, newSavedState, error as Error);
		}
	};

	// In the case of a successful save request,
	// we check whether the current save state matches the save state sent in the request.
	// (The user did not click again while the request was in progress)
	// If it does not match, we send a new request with the current value.
	const onSaveSuccess = (
		saveUrl: string,
		newSavedState: boolean,
		response: SavableSeriesItemData
	) => {
		setSeriesItemList((prevState: SeriesItemData[]) => {
			const item = prevState.find((item) => item.saveUrl === saveUrl) as
				| SavableSeriesItemData
				| undefined;

			if (item && item.saved !== newSavedState) {
				console.warn('There is a conflict with saved state: ', saveUrl);

				fetchSavedState(item, item.saved);
				return prevState;
			} else {
				return prevState.map((item) =>
					item.saveUrl === saveUrl
						? { ...response, isSaveRequestInProgress: false }
						: item
				);
			}
		});
	};

	// In the event of an error, we restore the save state before the request.
	// (regardless of whether the user has clicked since then)
	const onSaveError = (
		saveUrl: string,
		failedSavedState: boolean,
		error: Error
	) => {
		console.error('An error occurred while unsaving the item: ', saveUrl);
		console.error(error);
		if (error?.name === NO_COOKIES_ACCEPTED_ERROR) {
			createCookieToaster(saveConfig.translationMap.cookieToaster);
		}
		updateItems(saveUrl, {
			saved: !failedSavedState,
			isSaveRequestInProgress: false,
		});
		dispatchSavedChangedEvent(
			saveUrl,
			!failedSavedState,
			componentId + subcomponentId
		);
	};

	return changeSavedState;
};
